import React, {useState} from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import {submitForm} from '../../../services/formsService';
import Icon from '../../../components/AppIcon';

const BookDemoForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    company: '',
    role: '',
    teamSize: '',
    preferredDateTime: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState('');

  const teamSizeOptions = [
    {value: '1-10', label: '1–10'},
    {value: '11-50', label: '11–50'},
    {value: '51-200', label: '51–200'},
    {value: '200+', label: '200+'}
  ];

  const handleChange = (e) => {
    const {name, value} = e?.target;
    setFormData(prev => ({...prev, [name]: value}));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({...prev, [name]: ''}));
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({...prev, teamSize: value}));
    if (errors?.teamSize) {
      setErrors(prev => ({...prev, teamSize: ''}));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.workEmail?.trim()) {
      newErrors.workEmail = 'Email is required';
    }

    if (!formData?.company?.trim()) {
      newErrors.company = 'Company name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitStatus(null);
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitForm('demo', formData);

      if (result?.success) {
        setSubmitStatus('success');
        setSubmitMessage(result?.message);
        // Reset form
        setFormData({
          fullName: '',
          workEmail: '',
          company: '',
          role: '',
          teamSize: '',
          preferredDateTime: '',
          notes: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result?.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="flex items-start gap-3 p-4 rounded-md"
             style={{background: 'rgba(79, 179, 184, 0.1)', border: '1px solid rgba(79, 179, 184, 0.2)'}}>
          <Icon name="CheckCircle" size={20} style={{color: 'var(--color-success)'}} className="mt-0.5"/>
          <p className="text-sm" style={{color: 'var(--color-success)'}}>{submitMessage}</p>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="flex items-start gap-3 p-4 rounded-md"
             style={{background: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.2)'}}>
          <Icon name="AlertCircle" size={20} style={{color: 'var(--color-destructive)'}} className="mt-0.5"/>
          <p className="text-sm" style={{color: 'var(--color-destructive)'}}>{submitMessage}</p>
        </div>
      )}
      {/* Form Fields */}
      <Input
        label="Full Name"
        name="fullName"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
        placeholder="John Doe"
        disabled={isSubmitting}
      />
      <Input
        label="Work Email"
        name="workEmail"
        type="email"
        value={formData?.workEmail}
        onChange={handleChange}
        error={errors?.workEmail}
        required
        placeholder="john@company.com"
        disabled={isSubmitting}
      />
      <Input
        label="Company"
        name="company"
        value={formData?.company}
        onChange={handleChange}
        error={errors?.company}
        required
        placeholder="Your Company"
        disabled={isSubmitting}
      />
      <Input
        label="Role / Title"
        name="role"
        value={formData?.role}
        onChange={handleChange}
        placeholder="Engineering Manager (optional)"
        disabled={isSubmitting}
      />
      <Select
        label="Team Size"
        options={teamSizeOptions}
        value={formData?.teamSize}
        onChange={handleSelectChange}
        placeholder="Select team size (optional)"
        disabled={isSubmitting}
      />
      <Input
        label="Preferred Date/Time"
        name="preferredDateTime"
        value={formData?.preferredDateTime}
        onChange={handleChange}
        placeholder="e.g., Next Tuesday 2pm EST (optional)"
        disabled={isSubmitting}
      />
      <div className="space-y-2">
        <label
          htmlFor="notes"
          className="text-sm font-medium leading-none"
          style={{color: 'var(--color-foreground)', fontFamily: 'IBM Plex Sans'}}
        >
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData?.notes}
          onChange={handleChange}
          rows={4}
          className="flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: 'var(--color-card)',
            borderColor: 'var(--color-input)',
            color: 'var(--color-foreground)',
            fontFamily: 'IBM Plex Sans'
          }}
          placeholder="Any specific topics or questions? (optional)"
          disabled={isSubmitting}
        />
      </div>
      <Button
        type="submit"
        fullWidth
        loading={isSubmitting}
        disabled={isSubmitting}
        iconName="Calendar"
        iconPosition="right"
      >
        {isSubmitting ? 'Submitting...' : 'Request demo'}
      </Button>
    </form>
  );
};

export default BookDemoForm;